import { useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import { RefreshCw, FlaskConical, CheckCircle2, XCircle, Bookmark, BookmarkCheck, Loader2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { StatusBadge } from "@/components/shared/status-badge"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { JsonViewer } from "@/components/shared/json-viewer"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ShareLinkDialog } from "@/components/shared/share-link-dialog"
import { EventQuery, ReplayDryRunQuery, BookmarkByEventQuery } from "@/lib/graphql/queries"
import { ReplayEventMutation, CreateBookmarkMutation, DeleteBookmarkMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type EventDelivery = NonNullable<ResultOf<typeof EventQuery>["event"]>["deliveries"][number]

const deliveryColumns: Column<EventDelivery>[] = [
  {
    header: "Destination",
    cell: (row) => (
      <Link to={`/destinations/${row.destination.id}`} className="text-xs hover:underline">
        {row.destination.name}
      </Link>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

type DryRunMatch = ResultOf<typeof ReplayDryRunQuery>["replayDryRun"]["matches"][number]

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, reexecute] = useQuery({ query: EventQuery, variables: { id: id! } })
  const [replayResult, replay] = useMutation(ReplayEventMutation)
  const [dryRunOpen, setDryRunOpen] = useState(false)
  const [bookmarkResult, reexecuteBookmark] = useQuery({
    query: BookmarkByEventQuery,
    variables: { eventId: id! },
  })
  const [, createBookmark] = useMutation(CreateBookmarkMutation)
  const [, deleteBookmark] = useMutation(DeleteBookmarkMutation)

  const existingBookmark = bookmarkResult.data?.bookmarks.nodes[0] ?? null
  const [bookmarkOpen, setBookmarkOpen] = useState(false)
  const [unbookmarkOpen, setUnbookmarkOpen] = useState(false)
  const [bookmarkName, setBookmarkName] = useState("")
  const [bookmarkNotes, setBookmarkNotes] = useState("")
  const [bookmarkSaving, setBookmarkSaving] = useState(false)

  const handleCreateBookmark = async () => {
    setBookmarkSaving(true)
    const res = await createBookmark({
      eventId: id!,
      name: bookmarkName || undefined,
      notes: bookmarkNotes || undefined,
    })
    setBookmarkSaving(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Event bookmarked")
    setBookmarkOpen(false)
    setBookmarkName("")
    setBookmarkNotes("")
    reexecuteBookmark({ requestPolicy: "network-only" })
  }

  const handleRemoveBookmark = async () => {
    if (!existingBookmark) return
    const res = await deleteBookmark({ id: existingBookmark.id })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Bookmark removed")
    setUnbookmarkOpen(false)
    reexecuteBookmark({ requestPolicy: "network-only" })
  }

  const [dryRunLoading, setDryRunLoading] = useState(false)
  const [dryRunResult, executeDryRun] = useQuery({
    query: ReplayDryRunQuery,
    variables: { eventId: id! },
    pause: true,
  })

  if (dryRunLoading && (dryRunResult.data || dryRunResult.error)) {
    setDryRunLoading(false)
  }

  const event = result.data?.event

  const handleDryRun = () => {
    setDryRunLoading(true)
    setDryRunOpen(true)
    executeDryRun({ requestPolicy: "network-only" })
  }

  const handleReplay = async () => {
    const res = await replay({ eventId: id! })
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Replayed: ${res.data?.replayEvent.deliveries ?? 0} deliveries created`)
    reexecute({ requestPolicy: "network-only" })
  }

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!event) return <EmptyState title="Event not found" />

  let decodedBody: string | null = null
  try {
    decodedBody = event.rawBody ? atob(event.rawBody) : null
  } catch { /* malformed base64, leave null */ }
  let parsedBody: unknown = decodedBody
  if (decodedBody) {
    try { parsedBody = JSON.parse(decodedBody) } catch { /* use raw string */ }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{event.eventType || "Event Detail"}</h1>
          <StatusBadge status={event.status} />
        </div>
        <div className="flex items-center gap-2">
          <ShareLinkDialog scopeType="event" scopeId={id!} />
          <Button
          size="sm"
          variant="outline"
          onClick={() => existingBookmark ? setUnbookmarkOpen(true) : setBookmarkOpen(true)}
          aria-label={existingBookmark ? "Remove bookmark" : "Bookmark event"}
        >
          {existingBookmark ? (
            <BookmarkCheck className="h-3.5 w-3.5 mr-1.5 text-primary" />
          ) : (
            <Bookmark className="h-3.5 w-3.5 mr-1.5" />
          )}
          {existingBookmark ? "Bookmarked" : "Bookmark"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDryRun}
        >
          <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
          Dry Run
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleReplay}
          disabled={replayResult.fetching}
          className="shadow-primary-glow transition-shadow"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Replay
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 space-y-0 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Payload</CardTitle>
          </CardHeader>
          <CardContent>
            {parsedBody ? (
              <JsonViewer data={parsedBody} />
            ) : (
              <p className="text-sm text-muted-foreground">No body</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-border/30">
            <CardHeader>
              <CardTitle className={sectionTitleClass}>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <FieldLabel className="mb-1">ID</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs">{event.id}</code>
                  <CopyButton text={event.id} />
                </div>
              </div>
              <div>
                <FieldLabel className="mb-1">Source</FieldLabel>
                <Link to={`/sources/${event.source.id}`} className="text-xs hover:underline">
                  {event.source.name} ({event.source.slug})
                </Link>
              </div>
              <div>
                <FieldLabel className="mb-1">Idempotency Key</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs truncate">{event.idempotencyKey}</code>
                  <CopyButton text={event.idempotencyKey} />
                </div>
              </div>
              {event.channels && event.channels.length > 0 && (
                <div>
                  <FieldLabel className="mb-1">Channels</FieldLabel>
                  <div className="flex flex-wrap gap-1">
                    {event.channels.map((ch: string) => (
                      <Badge key={ch} variant="outline" className="text-2xs font-mono">{ch}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <FieldLabel className="mb-1">Received</FieldLabel>
                <TimeAgo date={event.receivedAt} />
              </div>
            </CardContent>
          </Card>

          {event.headers && (
            <Card className="border-border/30">
              <CardHeader>
                <CardTitle className={sectionTitleClass}>Headers</CardTitle>
              </CardHeader>
              <CardContent>
                <JsonViewer data={event.headers} collapsed />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Deliveries</h2>
        <DataTable
          columns={deliveryColumns}
          data={event.deliveries}
          keyFn={(row) => row.id}
          emptyTitle="No deliveries"
          onRowClick={(row) => navigate(`/deliveries/${row.id}`)}
        />
      </div>

      <Dialog open={dryRunOpen} onOpenChange={setDryRunOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Replay Dry Run</DialogTitle>
          </DialogHeader>
          {dryRunResult.fetching || dryRunLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : dryRunResult.error ? (
            <p className="text-sm text-destructive">{dryRunResult.error.message}</p>
          ) : (
            <div className="space-y-2">
              {dryRunResult.data?.replayDryRun.matches.length === 0 ? (
                <div className="text-center py-6">
                  <XCircle className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No subscriptions would match this event.</p>
                </div>
              ) : (
                dryRunResult.data?.replayDryRun.matches.map((match: DryRunMatch) => (
                  <div
                    key={match.subscription.id}
                    className={`flex items-center gap-3 rounded-lg border p-3 ${
                      match.wouldMatch
                        ? "border-emerald-500/30 bg-emerald-500/5"
                        : "border-border/50 bg-muted/30"
                    }`}
                  >
                    {match.wouldMatch ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-muted-foreground/60 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{match.subscription.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {match.destination.name}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-2xs shrink-0 ${
                        match.wouldMatch
                          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                          : "border-destructive/30 text-destructive/70"
                      }`}
                    >
                      {match.matchReason}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bookmarkOpen} onOpenChange={setBookmarkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bookmark Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bookmark-name">Name</Label>
              <Input
                id="bookmark-name"
                placeholder="e.g. Important payment event"
                value={bookmarkName}
                onChange={(e) => setBookmarkName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bookmark-notes">Notes</Label>
              <Textarea
                id="bookmark-notes"
                placeholder="Why are you bookmarking this event?"
                value={bookmarkNotes}
                onChange={(e) => setBookmarkNotes(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBookmarkOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateBookmark} disabled={bookmarkSaving}>
                {bookmarkSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                Save Bookmark
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={unbookmarkOpen}
        onOpenChange={setUnbookmarkOpen}
        title="Remove Bookmark"
        description="Are you sure you want to remove this bookmark? This action cannot be undone."
        confirmLabel="Remove"
        onConfirm={handleRemoveBookmark}
        destructive
      />
    </div>
  )
}
